import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Stack } from '@mui/material';
import { PaylinkResponse } from 'skipify-types';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetAllPaylinksQuery } from '../../../store/modules/paylink/api';
import { PaylinkActionsPopover, PaylinkFiltersPanel, PaylinkStatusChip } from '../components';
import { getDisplayedPaylinkAmount, getDisplayedPaylinkUserInfo } from '../../../util/helpers';
import SendPaylinkModal from '../SendPaylinkForm/modal';
import {
  GetAllPaylinksQueryFilters,
  GetAllPaylinksSortQuery,
  PaylinkCustomerInfoResponse,
} from '../../../store/modules/paylink/types';
import { sanitizeNumericString } from '../SendPaylinkForm/formValues';
import { useURLCustomerFetch } from '../hooks';
import { DownloadPaylinksDialog } from '../components/DownloadPaylinksDialog';
import {
  fromPaylinkPaymentMethodApiFilters,
  PaylinkFiltersFormValues,
} from '../../../hooks/usePaylinkFiltersForm';
import { useMerchant } from '../../../contexts/MerchantContext';
import { TableHeader } from './TableHeader';
import { DataTable, getSortableFields, Column } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';

const columns: Column<PaylinkResponse>[] = [
  {
    id: 'status',
    headerName: 'Status',
    renderCell: (row) => <PaylinkStatusChip status={row.status} size="small" />,
    maxWidth: 130,
    minWidth: 130,
  },
  {
    headerName: 'Customer',
    field: 'customer',
    valueGetter: (row) => getDisplayedPaylinkUserInfo(row).userInfo,
    maxWidth: 150,
  },
  {
    headerName: 'Date',
    id: 'createdAt_date',
    valueGetter: (row) => dayjs(row.createdAt).format('M/DD/YY'),
    maxWidth: 100,
    sortable: true,
    sortName: 'createdAt',
  },
  {
    headerName: 'Time',
    id: 'createdAt_time',
    valueGetter: (row) => dayjs(row.createdAt).format('h:mm A'),
    maxWidth: 100,
  },
  {
    field: 'merchantId',
    headerName: 'Merchant ID',
    maxWidth: 160,
    minWidth: 110,
  },
  {
    id: 'sendSms_sendEmail',
    headerName: 'Delivery',
    valueGetter: (row) => {
      const delivery: Array<'Text' | 'Email'> = [];
      if (row.sendSms) delivery.push('Text');
      if (row.sendEmail) delivery.push('Email');
      return delivery.join(', ');
    },
    maxWidth: 120,
  },
  {
    id: 'total',
    headerName: 'Amount',
    valueGetter: (row) => {
      return getDisplayedPaylinkAmount(row);
    },
    maxWidth: 140,
  },
  {
    field: 'orderId',
    headerName: 'Skipify Order ID',
    maxWidth: 170,
    minWidth: 170,
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 80,
    maxWidth: 150,
  },
  {
    field: 'merchantInvoiceId',
    headerName: 'Merchant Reference',
    minWidth: 160,
    maxWidth: 160,
  },
  {
    id: 'details',
    headerName: '',
    maxWidth: 60,
    minWidth: 60,
    renderCell: (row) => <PaylinkActionsPopover paylink={row} />,
  },
];

const sortableFields = getSortableFields(columns);

const filterKeys: Array<keyof GetAllPaylinksQueryFilters> = [
  'status',
  'paymentMethodType',
  'isFlexPay',
  'createdBefore',
  'createdAfter',
  'transactedBefore',
  'transactedAfter',
  'amountGTE',
  'amountLTE',
  'customerIds',
];

const convertToPanelProps = (
  initialFilterState: Record<string, string>,
  customers: PaylinkCustomerInfoResponse[],
  paymentMethodFilterEnabled: boolean,
): any => {
  const initialState: Partial<PaylinkFiltersFormValues> = {
    customers,
  };

  initialState.status = initialFilterState.status ? initialFilterState.status.split(',') : [];
  initialState.paymentMethodType = paymentMethodFilterEnabled
    ? fromPaylinkPaymentMethodApiFilters({
        paymentMethodType: initialFilterState.paymentMethodType,
        isFlexPay: initialFilterState.isFlexPay,
      })
    : [];

  if (initialFilterState.createdBefore) {
    initialState.createdBefore = dayjs(initialFilterState.createdBefore);
  }

  if (initialFilterState.createdAfter) {
    initialState.createdAfter = dayjs(initialFilterState.createdAfter);
  }

  if (initialFilterState.transactedBefore) {
    initialState.transactedBefore = dayjs(initialFilterState.transactedBefore);
  }

  if (initialFilterState.transactedAfter) {
    initialState.transactedAfter = dayjs(initialFilterState.transactedAfter);
  }

  if (initialFilterState.amountGTE) {
    initialState.amountGTE = `${initialFilterState.amountGTE}`;
  }

  if (initialFilterState.amountLTE) {
    initialState.amountLTE = `${initialFilterState.amountLTE}`;
  }

  return initialState;
};

export const ViewAllPaylinks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { paylinkPaymentMethodFilter } = useFlags();
  const { merchantId } = useMerchant();
  const initialCustomers = useURLCustomerFetch({ merchantId, customerIdKey: 'customerIds' });
  const [pageNumber, setPageNumber] = useState(
    Number(sanitizeNumericString(searchParams.get('pageNumber'))) || 1,
  );
  const [pageSize, setPageSize] = useState(
    Number(sanitizeNumericString(searchParams.get('pageSize'))) || 10,
  );
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [sortQuery, setSortQuery] = useState<GetAllPaylinksSortQuery>({
    sortBy: sortableFields.find((field) => field === searchParams.get('sortBy'))
      ? (searchParams.get('sortBy') as GetAllPaylinksSortQuery['sortBy'])
      : 'createdAt',
    orderBy: ['asc', 'desc'].find((order) => order === searchParams.get('orderBy'))
      ? (searchParams.get('orderBy') as GetAllPaylinksSortQuery['orderBy'])
      : 'desc',
  });

  const initialFilterState: Record<string, string> = {};
  filterKeys.forEach((key) => {
    if (searchParams.get(key)) {
      initialFilterState[key] = searchParams.get(key) as any;
    }
  });

  const [filters, setFilters] = useState<GetAllPaylinksQueryFilters>(initialFilterState);
  const activeFilters = useMemo(() => {
    if (paylinkPaymentMethodFilter) return filters;
    const filtersWithoutPaymentMethod = { ...filters };
    delete filtersWithoutPaymentMethod.paymentMethodType;
    delete filtersWithoutPaymentMethod.isFlexPay;
    return filtersWithoutPaymentMethod;
  }, [filters, paylinkPaymentMethodFilter]);

  const queryParams = useMemo(
    () => ({
      pageNumber,
      pageSize,
      ...activeFilters,
      ...sortQuery,
      search: searchQuery?.length > 0 ? searchQuery : undefined,
    }),
    [pageNumber, pageSize, activeFilters, sortQuery, searchQuery],
  );
  const {
    data: response,
    isFetching,
    refetch: refetchPaylinks,
  } = useGetAllPaylinksQuery({ merchantId, ...queryParams }, { skip: !merchantId });

  // Update the URL query params when the filters change
  useEffect(() => {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        searchParams.delete(key);
      } else if (typeof value === 'boolean') {
        searchParams.set(key, String(value));
      } else if (Array.isArray(value)) {
        if (value.length === 0) searchParams.delete(key);
        else searchParams.set(key, value.join(','));
      } else {
        searchParams.set(key, value as string);
      }
    });
    setSearchParams(searchParams, { replace: true });
  }, [queryParams, searchParams, setSearchParams]);

  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <SendPaylinkModal
        isOpen={isSendModalOpen}
        onClose={() => {
          setIsSendModalOpen(false);
        }}
      />
      <DownloadPaylinksDialog
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        merchantId={merchantId}
      />
      <Stack gap={2} sx={{ mt: 4 }}>
        <TableHeader
          refetchPaylinks={() => {
            setSearchResetKey((key) => key + 1);
            refetchPaylinks();
          }}
          isFetching={isFetching}
          setIsDownloadModalOpen={setIsDownloadModalOpen}
          setIsSendModalOpen={setIsSendModalOpen}
          onSearch={(query) => {
            setPageNumber(1);
            setSearchQuery(query);
          }}
          searchQuery={searchQuery}
          searchResetKey={searchResetKey}
          setIsFiltersPanelOpen={setIsFiltersPanelOpen}
          filtersCount={Object.keys(activeFilters).length}
        />
        <DataTable
          rows={response?.data ?? []}
          columns={columns}
          getRowId={(row) => row.paylinkId}
          isLoading={isFetching}
          pageSize={pageSize}
          sort={sortQuery}
          setSort={(query) => setSortQuery(query as GetAllPaylinksSortQuery)}
          tableHeight="calc(100vh - 315px)"
          noRowsMessage="No Paylinks found. Try changing the filters or search term."
          tableRef={tableRef}
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              onPageChange={(_, p) => {
                setPageNumber(p);
                tableRef.current?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }}
              page={pageNumber}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
              disabled={isFetching}
            />
          )}
        />
      </Stack>
      {(!initialFilterState.customerIds || !!initialCustomers.length) && (
        <PaylinkFiltersPanel
          isOpen={isFiltersPanelOpen}
          onClose={() => setIsFiltersPanelOpen(false)}
          merchantId={merchantId}
          onFiltersApply={(newFilters) => {
            setPageNumber(1);
            setFilters(newFilters);
          }}
          initialFilterState={convertToPanelProps(
            initialFilterState,
            initialCustomers,
            paylinkPaymentMethodFilter,
          )}
        />
      )}
    </>
  );
};
